import { Layout } from './CoreComponents';
import ConfirmationModal from './Shared/ConfirmationModal';
import ItemView from './Shared/ItemView';
import BaseGrid from './Shared/BaseGrid';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPORequests, getPORequestDetail, setPORequestRejected, setPORequestApprovedQuantity, setPORequestApproved, getStatusCounts } from '../store/slices/poRequestSlice';
import { getItem360List } from '../store/slices/itemSlice';
import { formatDate } from '../utils/formatterDate';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import { RootState, AppDispatch } from '../store/index';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import { useExpandedBatches } from '../hooks/useExpandedBatches';

const RequestEvaluationPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showError, showWarning } = useToast();
  const { user } = useUser();
  const { items: requests, loading } = useSelector((state: RootState) => state.poRequest);

  // Local state for main modal to preserve data when product detail modal opens
  const [mainItem360Data, setMainItem360Data] = useState<any>(null);
  const [mainItem360Loading, setMainItem360Loading] = useState(false);
  
  // Local state for product detail modal
  const [productItem360Data, setProductItem360Data] = useState<any>(null);
  const [productItem360Loading, setProductItem360Loading] = useState(false);

  const [showItemInquiry, setShowItemInquiry] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<{ id: number; itemName: string; itemCode: string; requestedQuantity: number; approvedQuantity: number; categoryName?: string; shortCode?: string; unit?: string | undefined; lastPurchasePrice?: number | undefined; lastPurchaseCurrency?: string | undefined; lastPurchaseDate?: string | undefined } | null>(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<{ id: number; itemName: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showItemViewModal, setShowItemViewModal] = useState(false);
  const [itemToView, setItemToView] = useState<any>(null);
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [productToView, setProductToView] = useState<any>(null);
  const [expandedBatches, setExpandedBatches] = useExpandedBatches('requestEvaluation_expandedBatches');
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showStartPurchaseModal, setShowStartPurchaseModal] = useState(false);
  const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
  // Load requests on component mount
  useEffect(() => {
    if (user?.id) {
      dispatch(getPORequests({ 
        RequestStatus: 'pending',
        ApproverUserId: user.id 
      }));
    }
  }, [dispatch, user?.id]);

  const openItemInquiry = useCallback(async (productId: string) => {
    const request = requests.find(req => req.id?.toString() === productId);
    if (request) {
      setCurrentRequest({
        id: request.id || 0,
        itemName: request.itemName || '',
        itemCode: request.itemCode || '',
        requestedQuantity: request.requestedQuantity || 0,
        approvedQuantity: request.approvedQuantity || 0,
        categoryName: request.categoryName || '',
        shortCode: request.shortCode || '',
        unit: request.unit || undefined,
        lastPurchasePrice: request.lastPurchasePrice || undefined,
        lastPurchaseCurrency: request.lastPurchaseCurrency || undefined,
        lastPurchaseDate: (request.lastPurchaseDate as unknown as string) || undefined
      });
    }
    setShowItemInquiry(true);

    try {
      // Redux store'dan detay verisini çek
      const resultAction = await dispatch(getPORequestDetail(parseInt(productId)));

      if (getPORequestDetail.fulfilled.match(resultAction)) {
        const response = resultAction.payload;
        const requestData = response.data; // Veriler data objesinin içinde
        // API response received

        // Update currentRequest with detailed data from API response
        setCurrentRequest({
          id: requestData.id || 0,
          itemName: requestData.itemName || '',
          itemCode: requestData.itemCode || '',
          requestedQuantity: requestData.requestedQuantity || 0,
          approvedQuantity: requestData.approvedQuantity || 0,
          categoryName: requestData.categoryName || '',
          shortCode: requestData.shortCode || '',
          unit: requestData.unit || undefined,
          lastPurchasePrice: requestData.lastPurchasePrice || undefined,
          lastPurchaseCurrency: requestData.lastPurchaseCurrency || undefined,
          lastPurchaseDate: requestData.lastPurchaseDate || undefined
        });

        // approvedQuantity boş değilse onu kullan, boş ise requestedQuantity kullan
        const quantity = requestData.approvedQuantity && requestData.approvedQuantity !== null
          ? requestData.approvedQuantity
          : requestData.requestedQuantity;

        // Set quantity input
        setQuantityInput(quantity?.toString() || '');
      } else {
        // API çağrısı başarısız olursa mevcut veriyi kullan
        setQuantityInput(request?.requestedQuantity?.toString() || '');
      }

      // Item 360 analizi için API çağrısı yap
      if (request?.itemId) {
        // Item 360 analysis API call
        // ID filtresi ile API çağrısı yap
        setMainItem360Loading(true);
        const item360Result = await dispatch(getItem360List({
          id: request.itemId,
          filterId: request.itemId // Aynı ID'yi filtre olarak da kullan
        }));

        // Store the result in main modal's local state
        if (getItem360List.fulfilled.match(item360Result)) {
          const responseData = item360Result.payload;
          if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            setMainItem360Data(responseData.data);
          } else {
            setMainItem360Data(responseData);
          }
        }
        setMainItem360Loading(false);
      } else {
        // No itemId found
        setMainItem360Data(null);
        setMainItem360Loading(false);
      }
    } catch (error) {
      // API call failed
      // Hata durumunda mevcut veriyi kullan
      setQuantityInput(request?.requestedQuantity?.toString() || '');
    }
  }, [requests, dispatch]);

  const closeItemInquiry = () => {
    setShowItemInquiry(false);
    setCurrentRequest(null);
    setQuantityInput('');
    setMainItem360Data(null);
    setMainItem360Loading(false);
  };

  const openItemView = useCallback((item: any) => {
    setItemToView(item);
    setShowItemViewModal(true);
  }, []);

  const closeItemViewModal = () => {
    setShowItemViewModal(false);
    setItemToView(null);
  };

  const openProductDetailModal = async (product: any) => {
    if (!product) return;
    
    setProductToView(product);
    setShowProductDetailModal(true);
    setProductItem360Loading(true);
    
    // Load detailed product data - API çağrısı yap
    try {
      // İlişkili mamullerden gelen product objesinde itemId'yi kullan
      if (product.itemId) {
        const resultAction = await dispatch(getItem360List({ 
          id: product.itemId,
          filterId: product.itemId // Aynı ID'yi filtre olarak da kullan
        }));
        
        // Store the result in product-specific state
        if (getItem360List.fulfilled.match(resultAction)) {
          const responseData = resultAction.payload;
          if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            setProductItem360Data(responseData.data);
          } else {
            setProductItem360Data(responseData);
          }
        }
        setProductItem360Loading(false);
      } else {
        console.warn('Product itemId not found for 360 analysis');
        setProductItem360Loading(false);
      }
    } catch (error) {
      console.error('Error loading product 360 data:', error);
      setProductItem360Loading(false);
    }
  };

  const closeProductDetailModal = () => {
    setShowProductDetailModal(false);
    setProductToView(null);
    setProductItem360Data(null);
    setProductItem360Loading(false);
  };

  const saveQuantity = async () => {
    if (quantityInput && currentRequest) {
      // Miktar validasyonu - negatif veya 0 olamaz
      const quantity = parseInt(quantityInput);
      if (quantity <= 0) {
        showWarning('Miktar 0 veya negatif olamaz. Lütfen geçerli bir miktar girin.');
        return;
      }
      
      try {
        // 1. Miktar > 0 ise setApprovedQuantity çağır
        if (quantity > 0) {
          const qtyResult = await dispatch(setPORequestApprovedQuantity({
            id: currentRequest.id,
            quantity: quantity
          }));
          
          if (!setPORequestApprovedQuantity.fulfilled.match(qtyResult)) {
            showError('Talep miktarı güncellenirken hata oluştu!');
            return;
          }
        }
        
        // 2. setApproved çağır
        const approveResult = await dispatch(setPORequestApproved(currentRequest.id));
        
        if (setPORequestApproved.fulfilled.match(approveResult)) {
          // Önce modalı kapat
          closeItemInquiry();
          // Sonra grid'i güncellemek için tekrar getPORequests çağır
          if (user?.id) {
            await dispatch(getPORequests({ 
              RequestStatus: 'pending',
              ApproverUserId: user.id 
            }));
            dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
          }
          
          // Başarı bildirimi göster
          showSuccess('Talep başarıyla onaylandı!');
        } else {
          // Error approving request
          showError('Talep onaylanırken bir hata oluştu!');
        }
      } catch (error) {
        // API call failed
        showError('Talep onaylanırken bir hata oluştu!');
      }
    }
  };

  const deleteRequest = useCallback((requestId: string) => {
    const request = requests.find(r => r.id?.toString() === requestId);
    if (request) {
      setRequestToDelete({
        id: request.id || 0,
        itemName: request.itemName || ''
      });
      setShowDeleteModal(true);
    }
  }, [requests]);

  const confirmDelete = async () => {
    if (requestToDelete) {
      setIsProcessingPurchase(true);
      try {
        await dispatch(setPORequestRejected(requestToDelete.id!));
        if (user?.id) {
          await dispatch(getPORequests({ 
            RequestStatus: 'pending',
            ApproverUserId: user.id 
          }));
          dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
        }
        setShowDeleteModal(false);
        setRequestToDelete(null);
        setSelectedRequests(prev => prev.filter(id => id !== requestToDelete.id?.toString()));
        if (showItemInquiry && currentRequest?.id === requestToDelete.id) {
          closeItemInquiry();
        }
        showSuccess('Talep başarıyla reddedildi!');
      } catch (error) {
        showError('Talep reddedilirken bir hata oluştu!');
      } finally {
        setIsProcessingPurchase(false);
      }
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedRequests.length === 0) return;
    setIsProcessingPurchase(true);
    try {
      for (const id of selectedRequests) {
        await dispatch(setPORequestRejected(parseInt(id)));
      }
      if (user?.id) {
        await dispatch(getPORequests({ 
          RequestStatus: 'pending',
          ApproverUserId: user.id 
        }));
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
      setShowBulkDeleteModal(false);
      setSelectedRequests([]);
      showSuccess(`${selectedRequests.length} talep başarıyla reddedildi!`);
    } catch (error) {
      showError('Talepler reddedilirken bir hata oluştu!');
    } finally {
      setIsProcessingPurchase(false);
    }
  };

  /** Seçili talepler için Satın Almayı Başlat - manuel akış: setApprovedQuantity (varsa) -> setApproved -> getPORequests */
  const handleStartPurchase = () => {
    if (selectedRequests.length === 0) return;
    const selectedRequestObjects = filteredRequests.filter(r =>
      selectedRequests.includes(r.id?.toString() || '')
    );
    
    // Validasyon: Miktarı 0 veya negatif olanları uyar
    const invalid = selectedRequestObjects.filter(r => {
      // approvedQuantity varsa ve > 0 ise onu kullan, yoksa requestedQuantity kullan
      const approvedQty = Number(r.approvedQuantity || 0);
      const requestedQty = Number(r.requestedQuantity || 0);
      const qty = approvedQty > 0 ? approvedQty : requestedQty;
      
      return qty <= 0;
    });
    
    if (invalid.length > 0) {
      showWarning('Bazı taleplerde geçerli miktar yok. Lütfen Değerlendir ile miktar girin veya seçimden çıkarın.');
      return;
    }

    setShowStartPurchaseModal(true);
  };

  const confirmStartPurchase = async () => {
    const selectedRequestObjects = filteredRequests.filter(r =>
      selectedRequests.includes(r.id?.toString() || '')
    );

    setIsProcessingPurchase(true);
    try {
      for (const request of selectedRequestObjects) {
        // approvedQuantity varsa ve > 0 ise onu kullan, yoksa requestedQuantity kullan
        const approvedQty = Number(request.approvedQuantity || 0);
        const requestedQty = Number(request.requestedQuantity || 0);
        const quantity = approvedQty > 0 ? approvedQty : requestedQty;
        
        // 1. Miktar > 0 ise setApprovedQuantity çağır
        if (quantity > 0) {
          const qtyResult = await dispatch(setPORequestApprovedQuantity({
            id: request.id!,
            quantity: quantity
          }));
          
          if (!setPORequestApprovedQuantity.fulfilled.match(qtyResult)) {
            showError(`Talep #${request.id} miktar güncellenirken hata oluştu!`);
            setIsProcessingPurchase(false);
            return;
          }
        }
        
        // 2. setApproved çağır
        const approveResult = await dispatch(setPORequestApproved(request.id!));
        if (!setPORequestApproved.fulfilled.match(approveResult)) {
           showError(`Talep #${request.id} onaylanırken hata oluştu!`);
           setIsProcessingPurchase(false);
           return;
        }
      }

      // 3. Listeyi yenile
      if (user?.id) {
        await dispatch(getPORequests({
          RequestStatus: 'pending',
          ApproverUserId: user.id
        }));
        dispatch(getStatusCounts({ CreatedBy: user.id, ApproverUserId: user.id, DecisionUserId: user.id, PurchaserId: user.id }));
      }
      
      setSelectedRequests([]);
      showSuccess(selectedRequestObjects.length === 1 ? 'Talep başarıyla onaylandı!' : `${selectedRequestObjects.length} talep başarıyla onaylandı!`);
      setShowStartPurchaseModal(false);
    } catch (error) {
      showError('Talep onaylanırken bir hata oluştu!');
    } finally {
      setIsProcessingPurchase(false);
    }
  };



  const filteredRequests = requests.filter(request => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesId = (request.id?.toString() || '').toLowerCase().includes(searchLower);
      const matchesShortCode = (request.shortCode?.toLowerCase() || '').includes(searchLower);
      const matchesItemCode = (request.itemCode?.toLowerCase() || '').includes(searchLower);
      const matchesItemName = (request.itemName?.toLowerCase() || '').includes(searchLower);
      const matchesCategoryName = (request.categoryName?.toLowerCase() || '').includes(searchLower);
      if (!matchesId && !matchesShortCode && !matchesItemCode && !matchesItemName && !matchesCategoryName) return false;
    }
    return true;
  }).sort((a, b) => {
    // Talep no'ya göre sıralama (desc - büyükten küçüğe)
    const idA = a.id || 0;
    const idB = b.id || 0;
    return idB - idA;
  });


  // Group requests by batchId
  const groupedByBatch = filteredRequests.reduce((acc, request) => {
    const batchId = (request as any).batchId || 0;
    if (!acc[batchId]) {
      acc[batchId] = [];
    }
    acc[batchId]!.push(request);
    return acc;
  }, {} as Record<number, typeof filteredRequests>);

  // Toggle batch expansion
  const toggleBatch = (batchId: number) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  // Toggle all batches
  const allBatchIds = Object.keys(groupedByBatch).map(id => parseInt(id));
  const isAllExpanded = allBatchIds.length > 0 && allBatchIds.every(id => expandedBatches.has(id));

  const toggleAllBatches = () => {
    if (isAllExpanded) {
      setExpandedBatches(new Set());
    } else {
      setExpandedBatches(new Set(allBatchIds));
    }
  };

  const childColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: '',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
    },
    {
      headerName: 'Talep No',
      field: 'id',
      minWidth: 70,
      flex: 0.5,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 80,
      flex: 0.6,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Kodu',
      field: 'itemCode',
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return '-';
        return (
          <span
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
            title={params.value?.startsWith('PRJ') ? 'Proje detayları' : 'Ürün detayları'}
            onClick={(e) => {
              e.stopPropagation();
              openItemView(params.data);
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      headerName: 'Kök Kod',
      valueGetter: (params) => params.data?.rootCode || '-',
      minWidth: 80,
      flex: 0.6,
    },
    {
      headerName: 'Kategori',
      field: 'categoryName',
      minWidth: 120,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Adı',
      field: 'itemName',
      minWidth: 180,
      flex: 1.5,
      tooltipField: 'itemName',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Teslim Tarihi',
      field: 'requestedDeliveryDate',
      minWidth: 100,
      flex: 0.7,
      valueFormatter: (params) => params.value ? formatDate(params.value) : '-',
    },
    {
      headerName: 'Talep Mikt.',
      field: 'requestedQuantity',
      minWidth: 90,
      flex: 0.6,
      type: 'rightAligned',
      valueFormatter: (params) => params.value ? `${Number(params.value).toLocaleString('tr-TR')} ${params.data?.unit || 'Adet'}` : '-',
    },
    {
      headerName: 'Not',
      valueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      minWidth: 120,
      flex: 1,
      tooltipValueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'İşlem',
      sortable: false,
      filter: false,
      resizable: false,
      minWidth: 100,
      flex: 0.8,
      cellRenderer: (params: ICellRendererParams) => (
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openItemInquiry(params.data?.id?.toString() || '');
            }}
            className="px-1 py-px rounded text-[10px] leading-tight transition-colors bg-blue-600 text-white hover:bg-blue-700"
          >
            Değerlendir
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteRequest(params.data?.id?.toString() || '');
            }}
            className="p-1 transition-colors text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            title="Sil"
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>
      ),
    },
  ], [openItemView, openItemInquiry, deleteRequest]);

  const onChildSelectionChanged = useCallback((event: any, batchRequests: any[]) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedIds = new Set(selectedRows.map((r: any) => r.id?.toString()).filter(Boolean));
    const batchIds = new Set(batchRequests.map((r: any) => r.id?.toString()).filter(Boolean));
    
    setSelectedRequests(prev => {
      const withoutBatch = prev.filter(id => !batchIds.has(id));
      return [...withoutBatch, ...Array.from(selectedIds)] as string[];
    });
  }, []);

  return (
    <Layout  currentScreen="request-evaluation">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-2 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
            {/* Search bar - dinamik genişlik */}
            <div className="flex-1 min-w-[300px]">
              <input 
                type="text" 
                placeholder="Talep No, Ürün Kodu, Kısa Kod, Kategori veya Ürün Adı ile Ara..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm" 
              />
            </div>
            {/* Yenile butonu */}
            <div className="flex flex-shrink-0">
              <button 
                onClick={() => {
                  if (user?.id) {
                    dispatch(getPORequests({ 
                      RequestStatus: 'pending',
                      ApproverUserId: user.id 
                    }));
                  }
                }}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <i className="fas fa-sync-alt mr-2"></i>Yenile
              </button>
            </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleStartPurchase}
                disabled={selectedRequests.length === 0}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-check mr-2"></i>Satın Almayı Başlat
              </button>
              <button
                type="button"
                onClick={() => selectedRequests.length > 0 && setShowBulkDeleteModal(true)}
                disabled={selectedRequests.length === 0}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-trash mr-2"></i>Sil
              </button>
            </div>
            
          </div>
        </div>


        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 flex flex-col max-h-[calc(100vh-300px)]">
          {/* Expandable Grid */}
          {loading ? (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                <i className="fas fa-spinner fa-spin text-4xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Yükleniyor...</h3>
              </div>
            </div>
          ) : Object.keys(groupedByBatch).length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                <i className="fas fa-inbox text-4xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {searchTerm ? 'Arama kriterlerinize uygun talep bulunamadı' : 'Henüz talep bulunmuyor'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm 
                    ? 'Farklı arama kriterleri deneyebilir veya arama kutusunu temizleyebilirsiniz.'
                    : 'Yeni talep oluşturmak için ilgili menüyü kullanabilirsiniz.'
                  }
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden overflow-auto flex-1">
                <div className="p-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{Object.keys(groupedByBatch).length} Parti · {selectedRequests.length} seçili</span>
                  <button
                    onClick={toggleAllBatches}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    {isAllExpanded ? 'Hepsini Daralt' : 'Hepsini Genişlet'}
                  </button>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {Object.entries(groupedByBatch).map(([batchIdStr, batchRequests]) => {
                    const batchId = parseInt(batchIdStr);
                    const isExpanded = expandedBatches.has(batchId);
                    const firstRequest = batchRequests[0];
                    const uniqueCategories = Array.from(new Set(batchRequests.map(r => r.categoryName).filter(Boolean))).join(', ');
                    const itemCount = batchRequests.length;
                    const batchSelectedCount = batchRequests.filter(r => selectedRequests.includes(r.id?.toString() || '')).length;

                    if (!firstRequest) return null;

                    return (
                      <div key={batchId}>
                        <div
                          className="px-4 py-3 flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                          onClick={() => toggleBatch(batchId)}
                        >
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-xs text-gray-500 dark:text-gray-400 w-4`}></i>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">Parti #{batchId || '-'}</span>
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">{itemCount} Kalem</span>
                              {batchSelectedCount > 0 && (
                                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded-full">{batchSelectedCount} seçili</span>
                              )}
                            </div>
                            <p className="text-xs text-blue-600 dark:text-blue-400 truncate mt-0.5">{uniqueCategories || '-'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{firstRequest.createdByName || `User ${firstRequest.createdBy}`}</div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">{firstRequest.createdAt ? formatDate(firstRequest.createdAt) : '-'}</div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="bg-white dark:bg-gray-800">
                            {batchRequests.map((req) => {
                              const reqId = req.id?.toString() || '';
                              const isSelected = selectedRequests.includes(reqId);
                              return (
                                <div
                                  key={req.id}
                                  className={`px-4 py-3 border-t border-gray-100 dark:border-gray-700/50 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                                >
                                  <div className="flex items-start gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => {
                                        setSelectedRequests(prev =>
                                          isSelected ? prev.filter(id => id !== reqId) : [...prev, reqId]
                                        );
                                      }}
                                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-1">
                                        <div className="min-w-0 flex-1">
                                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{req.itemName || '-'}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            <span
                                              className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
                                              onClick={(e) => { e.stopPropagation(); openItemView(req); }}
                                            >{req.itemCode || '-'}</span>
                                            {req.shortCode ? ` · ${req.shortCode}` : ''}
                                          </p>
                                        </div>
                                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 ml-2 flex-shrink-0">#{req.id}</span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                        <div className="flex justify-between">
                                          <span className="text-gray-500 dark:text-gray-400">Kategori:</span>
                                          <span className="text-gray-700 dark:text-gray-300">{req.categoryName || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500 dark:text-gray-400">Talep Mikt.:</span>
                                          <span className="text-gray-700 dark:text-gray-300">{req.requestedQuantity ? `${Number(req.requestedQuantity).toLocaleString('tr-TR')} ${req.unit || 'Adet'}` : '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-500 dark:text-gray-400">Teslim:</span>
                                          <span className="text-gray-700 dark:text-gray-300">{req.requestedDeliveryDate ? formatDate(req.requestedDeliveryDate) : '-'}</span>
                                        </div>
                                        {((req as any).note || (req as any).Note) && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Not:</span>
                                            <span className="text-gray-700 dark:text-gray-300 truncate ml-2">{(req as any).note ?? (req as any).Note}</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 mt-2">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); openItemInquiry(req.id?.toString() || ''); }}
                                          className="px-2 py-1 rounded text-xs transition-colors bg-blue-600 text-white hover:bg-blue-700"
                                        >
                                          Değerlendir
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); deleteRequest(req.id?.toString() || ''); }}
                                          className="p-1 transition-colors text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                          title="Sil"
                                        >
                                          <i className="fas fa-trash-alt text-xs"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Table + Grid Layout */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full border border-gray-300 dark:border-gray-600" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '36px' }}>
                        <button 
                          onClick={toggleAllBatches}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none"
                          title={isAllExpanded ? "Hepsini Daralt" : "Hepsini Genişlet"}
                        >
                          <i className={`fas fa-angle-double-${isAllExpanded ? 'up' : 'down'} text-xs`}></i>
                        </button>
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '72px' }}>Parti No</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Özet</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Oluşturan - Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedByBatch).map(([batchIdStr, batchRequests]) => {
                      const batchId = parseInt(batchIdStr);
                      const isExpanded = expandedBatches.has(batchId);
                      const firstRequest = batchRequests[0];
                      const uniqueCategories = Array.from(new Set(batchRequests.map(r => r.categoryName).filter(Boolean))).join(', ');
                      const itemCount = batchRequests.length;
                      
                      if (!firstRequest) return null;
                      
                      return (
                        <React.Fragment key={batchId}>
                          {/* Parent Row */}
                          <tr
                            className="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => toggleBatch(batchId)}
                          >
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center">
                              <i 
                                className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-[10px] text-gray-500 dark:text-gray-400`}
                              ></i>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs font-semibold text-gray-800 dark:text-gray-100">
                              {batchId || '-'}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col leading-tight">
                                <span className="font-medium text-gray-700 dark:text-gray-200">{uniqueCategories || '-'}</span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">{itemCount} Kalem</span>
                              </div>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col leading-tight">
                                <div className="font-medium text-gray-700 dark:text-gray-200">{firstRequest.createdByName || `User ${firstRequest.createdBy}`}</div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400">{firstRequest.createdAt ? formatDate(firstRequest.createdAt) : '-'}</div>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Child Grid - Expanded */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={4} className="border border-gray-300 dark:border-gray-600 p-0">
                                <div className="ag-child-grid">
                                  <BaseGrid
                                    gridStateKey="ag-grid-request-evaluation-child"
                                    rowData={batchRequests}
                                    columnDefs={childColumnDefs}
                                    getRowId={(params) => String(params.data.id)}
                                    rowSelection="multiple"
                                    onSelectionChanged={(event) => onChildSelectionChanged(event, batchRequests)}
                                    suppressNoRowsOverlay={true}
                                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

        </div>
      </div>

      {/* Item Inquiry Section */}
      {showItemInquiry && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={(e) => {
            // Backdrop'a tıklandığında modalı kapat (sadece Product Detail Modal açık değilse)
            if (e.target === e.currentTarget && !showProductDetailModal) {
              closeItemInquiry();
            }
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 w-full max-w-[95vw] sm:max-w-[900px] mx-auto max-h-[90vh] overflow-y-auto transition-colors duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">Ürün Analizi - 360°</h3>
              <button
                onClick={closeItemInquiry}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            <div className="space-y-3">
              {/* Product Header - Stok Adı */}
              <div className="text-left pb-2 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white transition-colors">
                  {currentRequest?.itemName || 'Stok Adı'}
                </h2>
              </div>

              {/* Product Information */}
              <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div><span>KISA KOD: <span className="font-medium text-gray-800 dark:text-white transition-colors">{currentRequest?.shortCode || '-'}</span></span></div>
                  <div><span>ÜRÜN KODU: <span className="font-medium text-gray-800 dark:text-white transition-colors">{currentRequest?.itemCode}</span></span></div>
                  <div><span>GRUP İSMİ: <span className="font-medium text-gray-800 dark:text-white transition-colors">{currentRequest?.categoryName}</span></span></div>
                </div>
              </div>

              {/* Tüketim/Satış Analizi */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                  {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 ? 'Tüketim Analizi' : 'Satış Analizi'}
                  {mainItem360Loading && (
                    <span className="ml-2 text-xs text-blue-600">
                      <i className="fas fa-spinner fa-spin mr-1"></i>Yükleniyor...
                    </span>
                  )}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 dark:border-gray-600">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-600">
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Dönem</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q1</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q2</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q3</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q4</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white dark:bg-gray-800">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">{new Date().getFullYear() - 1}</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q1 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q2 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q3 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q4 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.total ? Number(mainItem360Data.consumptionAnalysis.lastYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors"> {new Date().getFullYear()}</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q1 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q2 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q3 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q4 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.total ? Number(mainItem360Data.consumptionAnalysis.thisYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-blue-50 dark:bg-blue-900/20">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Değişim (%)</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q1 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q1}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q2 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q2}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q3 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q3}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q4 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q4}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.total ? `${mainItem360Data.consumptionAnalysis.changePercentage.total}%` : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Anlık Bilgiler */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">Anlık Bilgiler</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Stok</label>
                    <span className="text-base font-bold text-green-600">
                      {mainItem360Data?.instantInfo?.onHand ? Number(mainItem360Data.instantInfo.onHand).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Satınalma Siparişi</label>
                    <span className="text-base font-bold text-orange-600">
                      {mainItem360Data?.instantInfo?.openPurchaseBackOrder ? Number(mainItem360Data.instantInfo.openPurchaseBackOrder).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Talep</label>
                    <span className="text-base font-bold text-blue-600">
                      {mainItem360Data?.instantInfo?.openRequest ? Number(mainItem360Data.instantInfo.openRequest).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">
                      {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 ? 'Açık Üretim İş Emri' : 'Açık Müşteri Siparişi'}
                    </label>
                    <span className="text-base font-bold text-indigo-600">
                      {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 
                        ? (mainItem360Data?.instantInfo?.openProductionWO ? Number(mainItem360Data.instantInfo.openProductionWO).toLocaleString('tr-TR') : '0')
                        : (mainItem360Data?.instantInfo?.openSalesBackOrder ? Number(mainItem360Data.instantInfo.openSalesBackOrder).toLocaleString('tr-TR') : '0')
                      }
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Durum</label>
                    <span className="text-base font-bold text-purple-600">
                      {(() => {
                        if (!mainItem360Data?.instantInfo) return '0';
                        
                        const stok = mainItem360Data.instantInfo.onHand || 0;
                        const acikSatinalma = mainItem360Data.instantInfo.openPurchaseBackOrder || 0;
                        const acikTalep = mainItem360Data.instantInfo.openRequest || 0;
                        const acikMusteri = mainItem360Data.instantInfo.openSalesBackOrder || 0;
                        const acikUretim = mainItem360Data.instantInfo.openProductionWO || 0;
                        
                        // Durum = stok + açık satınalma siparişi + açık talep - açık müşteri siparişi/açık üretim iş emri
                        const cikarilacak = mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 ? acikUretim : acikMusteri;
                        const durum = stok + acikSatinalma + acikTalep - cikarilacak;
                        
                        // Negatif değerler için başına "-" işareti ekle
                        const formattedValue = Math.abs(durum).toLocaleString('tr-TR');
                        return durum < 0 ? `-${formattedValue}` : formattedValue;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Miktar Belirleme */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">

                <div className="space-y-2">
                  {/* Miktar Girişi ve Buton */}
                  <div className="flex flex-col sm:flex-row items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                        Onaylanacak Miktar
                        {mainItem360Data?.moq && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1 font-normal">
                            (Min: {Number(mainItem360Data.moq).toLocaleString('tr-TR')} {(currentRequest?.unit || 'adet').toLowerCase()})
                          </span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          min={mainItem360Data?.moq || 1}
                          value={quantityInput ? Number(quantityInput).toLocaleString('tr-TR') : ''}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/\./g, '');
                            if (rawValue === '') {
                              setQuantityInput('');
                            } else {
                              const numValue = parseFloat(rawValue);
                              if (!isNaN(numValue) && numValue >= 0) {
                                setQuantityInput(numValue.toString());
                              }
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            const rawValue = e.target.value.replace(/\./g, '');
                            const minValue = mainItem360Data?.moq || 1;
                            if (rawValue === '') {
                              setQuantityInput(minValue.toString());
                            } else {
                              const numValue = parseFloat(rawValue);
                              if (isNaN(numValue) || numValue < minValue) {
                                setQuantityInput(minValue.toString());
                              }
                            }
                          }}
                          className="w-full px-3 py-2 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder={`Min: ${mainItem360Data?.moq || 1} ${(currentRequest?.unit || 'adet').toLowerCase()}`}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {currentRequest?.unit || 'Adet'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <button
                        onClick={saveQuantity}
                        disabled={!quantityInput || parseInt(quantityInput) < (mainItem360Data?.moq || 1)}
                        className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="fas fa-check mr-1"></i>Satın Almayı Başlat
                      </button>
                      <button
                        onClick={() => {
                          if (currentRequest?.id) {
                            deleteRequest(currentRequest.id.toString());
                          }
                        }}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                      >
                        <i className="fas fa-trash mr-1"></i>Sil
                      </button>
                    </div>
                  </div>

                  {/* Son Satın Alma Fiyatı - Metin Formatı */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      {(() => {
                        const lastPurchasePrice = mainItem360Data?.lastPurchasePrice || currentRequest?.lastPurchasePrice;
                        const lastPurchaseDate = mainItem360Data?.lastPurchaseDate || currentRequest?.lastPurchaseDate;
                        const lastPurchaseCurrency = mainItem360Data?.lastPurchaseCurrency || currentRequest?.lastPurchaseCurrency || 'TRY';
                        const unitPriceUSDValue = mainItem360Data?.unitPriceUSDValue;
                        const lastPurchaseQuantity = mainItem360Data?.lastPurchaseQuantity;
                        
                        if (lastPurchasePrice && lastPurchaseDate && lastPurchaseDate !== '0001-01-01T00:00:00') {
                          return (
                            <>
                              Bu ürün <span className="font-semibold text-blue-600">{parseFloat(String(lastPurchasePrice)).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {lastPurchaseCurrency}</span>
                              {unitPriceUSDValue && (
                                <> (<span className="font-semibold text-green-600">${parseFloat(String(unitPriceUSDValue)).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>)</>
                              )}
                              {' '}ye <span className="font-semibold">{formatDate(lastPurchaseDate)}</span> tarihinde{lastPurchaseQuantity != null ? <> <span className="font-semibold">{Number(lastPurchaseQuantity).toLocaleString('tr-TR')} {(currentRequest?.unit || 'adet').toLowerCase()}</span> satın alınmıştır.</> : ' satın alınmıştır.'}
                            </>
                          );
                        } else {
                          return <span className="text-gray-500 dark:text-gray-400">Son satın alma bilgisi bulunmamaktadır.</span>;
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* İlişkili Mamuller - Sadece veri varsa göster */}
              {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">İlişkili Mamuller</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-600">
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Mamul Kodu</th>
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Mamul Adı</th>
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Miktar</th>
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Bu Yıl</th>
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Önceki Yıl</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mainItem360Data?.relatedProducts?.map((product: any, index: number) => (
                          <tr key={index} className="bg-white dark:bg-gray-800">
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 transition-colors">
                              {product.productCode || '-'}
                            </td>
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 transition-colors">
                              <span 
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
                                onClick={() => openProductDetailModal(product)}
                              >
                                {product.productName || '-'}
                              </span>
                            </td>
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                              {product.quantity ? Number(product.quantity).toLocaleString('tr-TR') : '-'}
                            </td>
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                              {product.thisYearTotal != null ? Number(product.thisYearTotal).toLocaleString('tr-TR') : '-'}
                            </td>
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                              {product.lastYearTotal != null ? Number(product.lastYearTotal).toLocaleString('tr-TR') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ItemView Modal */}
      {showItemViewModal && itemToView && (
        <ItemView
          isOpen={showItemViewModal}
          onClose={closeItemViewModal}
          itemId={itemToView.itemId}
          mode="view"
        />
      )}

      {/* Product Detail Modal */}
      {showProductDetailModal && productToView && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={(e) => {
            // Backdrop'a tıklandığında sadece bu modalı kapat, ana modalı kapatma
            if (e.target === e.currentTarget) {
              closeProductDetailModal();
            }
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 w-full max-w-[95vw] sm:max-w-[900px] mx-auto max-h-[90vh] overflow-y-auto transition-colors duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">Ürün Analizi - 360°</h3>
              <button
                onClick={closeProductDetailModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            <div className="space-y-3">
              {/* Product Header - Stok Adı */}
              <div className="text-left pb-2 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white transition-colors">
                  {productItem360Data?.itemName || productToView?.productName || 'Stok Adı'}
                </h2>
              </div>

              {/* Product Information */}
              <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div><span>KISA KOD: <span className="font-medium text-gray-800 dark:text-white transition-colors">{productItem360Data?.shortCode || '-'}</span></span></div>
                  <div><span>ÜRÜN KODU: <span className="font-medium text-gray-800 dark:text-white transition-colors">{productItem360Data?.itemCode || productToView?.productCode || '-'}</span></span></div>
                  <div><span>GRUP İSMİ: <span className="font-medium text-gray-800 dark:text-white transition-colors">{productItem360Data?.categoryName || '-'}</span></span></div>
                </div>
              </div>

              {/* Tüketim/Satış Analizi */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                  {productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 ? 'Tüketim Analizi' : 'Satış Analizi'}
                  {productItem360Loading && (
                    <span className="ml-2 text-xs text-blue-600">
                      <i className="fas fa-spinner fa-spin mr-1"></i>Yükleniyor...
                    </span>
                  )}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 dark:border-gray-600">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-600">
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Dönem</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q1</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q2</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q3</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q4</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white dark:bg-gray-800">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Geçen Yıl {new Date().getFullYear() - 1}</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q1 ? Number(productItem360Data.consumptionAnalysis.lastYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q2 ? Number(productItem360Data.consumptionAnalysis.lastYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q3 ? Number(productItem360Data.consumptionAnalysis.lastYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q4 ? Number(productItem360Data.consumptionAnalysis.lastYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.total ? Number(productItem360Data.consumptionAnalysis.lastYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Bu Yıl {new Date().getFullYear()}</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q1 ? Number(productItem360Data.consumptionAnalysis.thisYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q2 ? Number(productItem360Data.consumptionAnalysis.thisYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q3 ? Number(productItem360Data.consumptionAnalysis.thisYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q4 ? Number(productItem360Data.consumptionAnalysis.thisYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.total ? Number(productItem360Data.consumptionAnalysis.thisYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-blue-50 dark:bg-blue-900/20">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Değişim (%)</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q1 ? `${productItem360Data.consumptionAnalysis.changePercentage.q1}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q2 ? `${productItem360Data.consumptionAnalysis.changePercentage.q2}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q3 ? `${productItem360Data.consumptionAnalysis.changePercentage.q3}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q4 ? `${productItem360Data.consumptionAnalysis.changePercentage.q4}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.total ? `${productItem360Data.consumptionAnalysis.changePercentage.total}%` : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Anlık Bilgiler */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">Anlık Bilgiler</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Stok</label>
                    <span className="text-base font-bold text-green-600">
                      {productItem360Data?.instantInfo?.onHand ? Number(productItem360Data.instantInfo.onHand).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Satınalma Siparişi</label>
                    <span className="text-base font-bold text-orange-600">
                      {productItem360Data?.instantInfo?.openPurchaseBackOrder ? Number(productItem360Data.instantInfo.openPurchaseBackOrder).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Talep</label>
                    <span className="text-base font-bold text-blue-600">
                      {productItem360Data?.instantInfo?.openRequest ? Number(productItem360Data.instantInfo.openRequest).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">
                      {productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 ? 'Açık Üretim İş Emri' : 'Açık Müşteri Siparişi'}
                    </label>
                    <span className="text-base font-bold text-indigo-600">
                      {productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 
                        ? (productItem360Data?.instantInfo?.openProductionWO ? Number(productItem360Data.instantInfo.openProductionWO).toLocaleString('tr-TR') : '0')
                        : (productItem360Data?.instantInfo?.openSalesBackOrder ? Number(productItem360Data.instantInfo.openSalesBackOrder).toLocaleString('tr-TR') : '0')
                      }
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Durum</label>
                    <span className="text-base font-bold text-purple-600">
                      {(() => {
                        if (!productItem360Data?.instantInfo) return '0';
                        
                        const stok = productItem360Data.instantInfo.onHand || 0;
                        const acikSatinalma = productItem360Data.instantInfo.openPurchaseBackOrder || 0;
                        const acikTalep = productItem360Data.instantInfo.openRequest || 0;
                        const acikMusteri = productItem360Data.instantInfo.openSalesBackOrder || 0;
                        const acikUretim = productItem360Data.instantInfo.openProductionWO || 0;
                        
                        // Durum = stok + açık satınalma siparişi + açık talep - açık müşteri siparişi/açık üretim iş emri
                        const cikarilacak = productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 ? acikUretim : acikMusteri;
                        const durum = stok + acikSatinalma + acikTalep - cikarilacak;
                        
                        // Negatif değerler için başına "-" işareti ekle
                        const formattedValue = Math.abs(durum).toLocaleString('tr-TR');
                        return durum < 0 ? `-${formattedValue}` : formattedValue;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Son Satın Alma Fiyatı - Metin Formatı */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  {(() => {
                    const lastPurchasePrice = productItem360Data?.lastPurchasePrice;
                    const lastPurchaseDate = productItem360Data?.lastPurchaseDate;
                    const lastPurchaseCurrency = productItem360Data?.lastPurchaseCurrency;
                    const unitPriceUSDValue = productItem360Data?.unitPriceUSDValue;
                    const lastPurchaseQuantity = productItem360Data?.lastPurchaseQuantity;
                    
                    if (lastPurchasePrice && lastPurchaseCurrency) {
                      const quantityText = lastPurchaseQuantity != null ? ` ${Number(lastPurchaseQuantity).toLocaleString('tr-TR')} ${(productItem360Data?.unit || 'adet').toLowerCase()}` : '';
                      return (
                        <>
                          Bu ürün <span className="font-semibold text-blue-600">{parseFloat(String(lastPurchasePrice)).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} {lastPurchaseCurrency}</span>
                          {unitPriceUSDValue && (
                            <> (<span className="font-semibold text-green-600">${parseFloat(String(unitPriceUSDValue)).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>)</>
                          )}
                          {lastPurchaseDate && lastPurchaseDate !== '0001-01-01T00:00:00' ? (
                            <> fiyatında <span className="font-semibold">{formatDate(lastPurchaseDate)}</span> tarihinde{quantityText ? <><span className="font-semibold">{quantityText}</span> satın alınmıştır.</> : ' satın alınmıştır.'}</>
                          ) : (
                            <> fiyatında{quantityText ? <><span className="font-semibold">{quantityText}</span> satın alınmıştır.</> : ' satın alınmıştır.'}</>
                          )}
                        </>
                      );
                    } else {
                      return <span className="text-gray-500 dark:text-gray-400">Son satın alma bilgisi bulunmamaktadır.</span>;
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => !isProcessingPurchase && setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Talep Silme Onayı"
        message={isProcessingPurchase ? "İşlem yapılıyor..." : `${requestToDelete?.itemName} talebini silmek istediğinizden emin misiniz?`}
        confirmText={isProcessingPurchase ? "Siliniyor..." : "Sil"}
        type="danger"
        icon={isProcessingPurchase ? "fas fa-spinner fa-spin" : "fas fa-trash"}
        disabled={isProcessingPurchase}
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkDeleteModal}
        onClose={() => !isProcessingPurchase && setShowBulkDeleteModal(false)}
        onConfirm={confirmBulkDelete}
        title="Toplu Talep Silme Onayı"
        message={isProcessingPurchase ? "İşlem yapılıyor..." : `Seçili ${selectedRequests.length} talebi silmek istediğinize emin misiniz?`}
        confirmText={isProcessingPurchase ? "Siliniyor..." : "Sil"}
        type="danger"
        icon={isProcessingPurchase ? "fas fa-spinner fa-spin" : "fas fa-trash"}
        disabled={isProcessingPurchase}
      />

      {/* Start Purchase Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStartPurchaseModal}
        onClose={() => !isProcessingPurchase && setShowStartPurchaseModal(false)}
        onConfirm={confirmStartPurchase}
        title="Satın Almayı Başlatma Onayı"
        message={isProcessingPurchase ? "İşlem yapılıyor, lütfen bekleyin..." : `Seçili ${selectedRequests.length} talep için satın alma sürecini başlatmak istediğinize emin misiniz?`}
        confirmText={isProcessingPurchase ? "İşleniyor..." : "Başlat"}
        type="success"
        icon={isProcessingPurchase ? "fas fa-spinner fa-spin" : "fas fa-shopping-cart"}
        disabled={isProcessingPurchase}
      />

      {/* Global Loading Overlay */}
      {isProcessingPurchase && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-[9999] flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl flex flex-col items-center border border-gray-200 dark:border-gray-700">
            <div className="relative w-16 h-16 mb-4">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-900 dark:text-white font-medium text-lg">İşlem Yapılıyor</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Lütfen pencereyi kapatmayın...</p>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default RequestEvaluationPage;
